import React, { useState } from 'react'
import {
    Box,
    ButtonBase,
    makeStyles,
    Popover,
    Typography,
} from '@material-ui/core'
import {
    ArrowDownward as ArrowDownIcon,
    ArrowUpward as ArrowUpIcon,
} from '@material-ui/icons'
import {
    AuditSnapshotRow,
    HEALTH_STATUS_BUCKETS,
    computeMetricDeltaAbs,
    getHealthStatusCounts,
} from './auditMetrics'

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(0.5),
        width: '100%',
        padding: theme.spacing(0.25, 0),
        borderRadius: 4,
        textAlign: 'left',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    bar: {
        display: 'flex',
        height: 10,
        width: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: theme.palette.grey[200],
    },
    segment: {
        height: '100%',
        minWidth: 0,
    },
    legend: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        justifyContent: 'flex-end',
        rowGap: 2,
    },
    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
    },
    swatch: {
        width: 6,
        height: 6,
        borderRadius: 1,
        flexShrink: 0,
    },
    hint: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        textAlign: 'right',
    },
    popover: {
        padding: theme.spacing(1.5, 2),
        minWidth: 220,
    },
    popoverTitle: {
        fontWeight: 600,
        fontSize: '0.85rem',
        marginBottom: theme.spacing(1),
    },
    detailRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(0.5, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    detailLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0,
    },
    detailSwatch: {
        width: 8,
        height: 8,
        borderRadius: 2,
        flexShrink: 0,
    },
    detailValues: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    detailCount: {
        fontWeight: 600,
        fontSize: '0.85rem',
        minWidth: 28,
        textAlign: 'right',
    },
    deltaUp: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: theme.palette.success.main,
        fontSize: '0.7rem',
        minWidth: 42,
        justifyContent: 'flex-end',
    },
    deltaDown: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        color: theme.palette.error.main,
        fontSize: '0.7rem',
        minWidth: 42,
        justifyContent: 'flex-end',
    },
    deltaFlat: {
        fontSize: '0.7rem',
        color: theme.palette.text.disabled,
        minWidth: 42,
        textAlign: 'right',
    },
}))

interface Props {
    metrics: AuditSnapshotRow['metrics']
    previousMetrics?: AuditSnapshotRow['metrics'] | null
}

export const HealthStatusCell: React.FC<Props> = ({
    metrics,
    previousMetrics = null,
}) => {
    const classes = useStyles()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const counts = getHealthStatusCounts(metrics)
    const prevCounts = previousMetrics
        ? getHealthStatusCounts(previousMetrics)
        : null
    const total = HEALTH_STATUS_BUCKETS.reduce(
        (sum, bucket) => sum + counts[bucket.key],
        0,
    )

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <ButtonBase
                className={classes.root}
                onClick={handleOpen}
                aria-label="View health status details"
            >
                <Box className={classes.bar}>
                    {total > 0 ? (
                        HEALTH_STATUS_BUCKETS.map((bucket) => {
                            const count = counts[bucket.key]
                            if (count <= 0) {
                                return null
                            }
                            return (
                                <Box
                                    key={bucket.key}
                                    className={classes.segment}
                                    style={{
                                        flexGrow: count,
                                        backgroundColor: bucket.color,
                                    }}
                                />
                            )
                        })
                    ) : (
                        <Box
                            className={classes.segment}
                            style={{ flexGrow: 1 }}
                        />
                    )}
                </Box>
                <Box className={classes.legend}>
                    {HEALTH_STATUS_BUCKETS.map((bucket) => (
                        <span key={bucket.key} className={classes.legendItem}>
                            <span
                                className={classes.swatch}
                                style={{ backgroundColor: bucket.color }}
                            />
                            {bucket.shortLabel} {counts[bucket.key]}
                        </span>
                    ))}
                </Box>
                <Typography className={classes.hint}>Details</Typography>
            </ButtonBase>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box className={classes.popover}>
                    <Typography className={classes.popoverTitle}>
                        Health Status
                    </Typography>
                    {HEALTH_STATUS_BUCKETS.map((bucket) => {
                        const count = counts[bucket.key]
                        const prev = prevCounts ? prevCounts[bucket.key] : null
                        const delta = prevCounts
                            ? computeMetricDeltaAbs(count, prev)
                            : null
                        return (
                            <Box key={bucket.key} className={classes.detailRow}>
                                <Box className={classes.detailLabel}>
                                    <span
                                        className={classes.detailSwatch}
                                        style={{
                                            backgroundColor: bucket.color,
                                        }}
                                    />
                                    <Typography variant="body2">
                                        {bucket.label}
                                    </Typography>
                                </Box>
                                <Box className={classes.detailValues}>
                                    <Typography
                                        className={classes.detailCount}
                                        component="span"
                                    >
                                        {count.toLocaleString()}
                                    </Typography>
                                    {delta === null || delta === 0 ? (
                                        <span className={classes.deltaFlat}>
                                            {delta === 0 ? '—' : ''}
                                        </span>
                                    ) : (
                                        <span
                                            className={
                                                delta > 0
                                                    ? classes.deltaUp
                                                    : classes.deltaDown
                                            }
                                        >
                                            {delta > 0 ? (
                                                <ArrowUpIcon
                                                    style={{ fontSize: 12 }}
                                                />
                                            ) : (
                                                <ArrowDownIcon
                                                    style={{ fontSize: 12 }}
                                                />
                                            )}
                                            {delta > 0 ? '+' : ''}
                                            {delta}
                                        </span>
                                    )}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>
            </Popover>
        </>
    )
}
